import * as React from "react";
import clsx from "clsx";
import { MessageSquare, MessageSquareOff, MoreVertical, Pencil, Trash } from "lucide-react";
import { Loader } from "@/components/ui/loader";
import { useNavigate } from "react-router";
import { useToggle } from "react-use";

import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useGetRecentQAConversationsQuery } from "@/modules/recent-qa/api";
import { RecentQAConversation } from "@/modules/recent-qa/types";
import { RenameConversationModal } from "@/modules/recent-qa/components/RenameConversationModal";
import { DeleteConversationModal } from "@/modules/recent-qa/components/DeleteConversationModal";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Props {
  systemId: string;
  currentConversationId?: string;
  basePath?: string;
}

export const QAHistoryPanel: React.FC<Props> = ({ systemId, currentConversationId, basePath }) => {
  const resolvedBasePath = basePath ?? `/chat/${systemId}`;
  const navigate = useNavigate();
  const { formatDateTime } = useDateFormatter();
  const { data, isLoading } = useGetRecentQAConversationsQuery({
    fse_entry_id: systemId,
    limit: 50,
  });

  const [isRenameOpen, toggleIsRenameOpen] = useToggle(false);
  const [isDeleteOpen, toggleIsDeleteOpen] = useToggle(false);
  const [selectedConversation, setSelectedConversation] = React.useState<RecentQAConversation | null>(null);

  const handleRename = (conversation: RecentQAConversation) => {
    setSelectedConversation(conversation);
    toggleIsRenameOpen(true);
  };

  const handleDelete = (conversation: RecentQAConversation) => {
    setSelectedConversation(conversation);
    toggleIsDeleteOpen(true);
  };

  const handleDeleted = () => {
    if (selectedConversation?.conversation_id === currentConversationId) {
      navigate(resolvedBasePath);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader className="h-6 w-6 text-gray-400" />
      </div>
    );
  }

  if (!data || data.results.length === 0) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 px-4 text-muted-foreground">
        <MessageSquareOff className="h-8 w-8" />
        <p className="text-sm font-medium">No previous conversations</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col overflow-y-auto">
        {data.results.map(conversation => {
          const isCurrent = conversation.conversation_id === currentConversationId;
          const displayText = conversation.name ?? conversation.last_message;
          const truncated = displayText && displayText.length > 80 ? `${displayText.slice(0, 80)}...` : displayText;

          return (
            <button
              key={conversation.conversation_id}
              onClick={() => navigate(`${resolvedBasePath}/${conversation.conversation_id}`)}
              disabled={isCurrent}
              className={clsx(
                "group flex items-start gap-3 border-b border-input px-4 py-3 text-left transition-colors",
                {
                  "bg-ivory-mist-200": isCurrent,
                  "hover:bg-ivory-mist-100": !isCurrent,
                },
              )}
            >
              <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                {truncated && <p className="truncate text-sm">{truncated}</p>}
                <p className="mt-0.5 text-xs text-muted-foreground">{formatDateTime(conversation.updated_at)}</p>
              </div>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-6 w-6 shrink-0 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <MoreVertical size={14} className="text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => handleRename(conversation)} className="cursor-pointer">
                    <Pencil className="mr-2 h-4 w-4" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => handleDelete(conversation)} className="cursor-pointer">
                    <Trash className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </button>
          );
        })}
      </div>

      {selectedConversation && (
        <>
          <RenameConversationModal
            isOpen={isRenameOpen}
            setIsOpen={toggleIsRenameOpen}
            conversation={selectedConversation}
          />
          <DeleteConversationModal
            isOpen={isDeleteOpen}
            setIsOpen={toggleIsDeleteOpen}
            conversation={selectedConversation}
            onDeleted={handleDeleted}
          />
        </>
      )}
    </>
  );
};
